import React, { useState, useCallback } from 'react';
import { TestResult, KITS, WaterBodyType } from '../types';

// ─── Shared UI primitives ────────────────────────────────────────────────────

interface CardProps {
  children: React.ReactNode;
  className?: string;
}
const Card: React.FC<CardProps> = ({ children, className }) => (
  <div className={`bg-slate-800 p-4 rounded-lg shadow-md ${className ?? ''}`}>{children}</div>
);

interface GaugeProps {
  value: number;
  min: number;
  max: number;
  ranges: { color: string; width: string; label: string }[];
}
const Gauge: React.FC<GaugeProps> = ({ value, min, max, ranges }) => {
  const percent = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return (
    <div className="mt-3">
      <div className="w-full h-3.5 rounded-full flex relative bg-slate-700">
        {ranges.map((range, index) => (
          <div
            key={index}
            className={`${range.color} ${index === 0 ? 'rounded-l-full' : ''} ${index === ranges.length - 1 ? 'rounded-r-full' : ''}`}
            style={{ width: range.width }}
          />
        ))}
        <div
          className="absolute top-[-2px] w-1 h-5 bg-white rounded-full border-2 border-slate-900 transition-all duration-500 ease-out"
          style={{ left: `${percent}%` }}
        />
      </div>
      <div className="text-xs text-slate-400 flex justify-between mt-1 px-1">
        {ranges.map((range, index) => (
          <span key={index}>{range.label}</span>
        ))}
      </div>
    </div>
  );
};

interface DetailsProps {
  title: React.ReactNode;
  children: React.ReactNode;
  startOpen?: boolean;
  className?: string;
}
const Details: React.FC<DetailsProps> = ({ title, children, startOpen = false, className = '' }) => (
  <details open={startOpen} className={`group ${className}`}>
    <summary className="cursor-pointer flex justify-between items-center list-none">
      {title}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5 text-cyan-400 transition-transform duration-200 group-open:rotate-90"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </summary>
    <div className="mt-4 text-slate-300 text-sm space-y-2 border-t border-slate-700 pt-3">{children}</div>
  </details>
);

// ─── Component ──────────────────────────────────────────────────────────────

/** Compact "Tested / Not Tested" toggle shown on each parameter entry card. */
interface TestedBadgeProps {
  tested: boolean;
  onToggle: (v: boolean) => void;
}
const TestedBadge: React.FC<TestedBadgeProps> = ({ tested, onToggle }) => (
  <label className="flex items-center gap-1.5 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={tested}
      onChange={(e) => onToggle(e.target.checked)}
      className="rounded accent-cyan-500 w-4 h-4"
    />
    <span className={`text-xs font-semibold ${tested ? 'text-cyan-400' : 'text-slate-500'}`}>
      {tested ? '✓ Tested' : 'Not Tested'}
    </span>
  </label>
);

interface NewTestTabProps {
  poolVolume: number;
  setPoolVolume: (volume: number) => void;
  onSave: (results: TestResult) => void;
  waterBody: WaterBodyType;
}

export const NewTestTab: React.FC<NewTestTabProps> = ({ poolVolume, setPoolVolume, onSave, waterBody }) => {
  const isHotTub = waterBody === 'hot_tub';
  const defaultKit = isHotTub ? 'K-1515' : 'K-2006';

  const [kit, setKit] = useState<string>(defaultKit);
  const [fcSampleSize, setFcSampleSize] = useState(0.5);
  const [fcDrops, setFcDrops] = useState(0);
  const [ccDrops, setCcDrops] = useState(0);
  const [ph, setPh] = useState(7.5);
  const [acidDemandDrops, setAcidDemandDrops] = useState(0);
  const [baseDemandDrops, setBaseDemandDrops] = useState(0);
  const [bromine, setBromine] = useState(0);
  const [taDrops, setTaDrops] = useState(0);
  const [chDrops, setChDrops] = useState(0);
  const [cya, setCya] = useState(isHotTub ? 30 : 40);
  const [temp, setTemp] = useState(isHotTub ? 102 : 82);
  const [tds, setTds] = useState(500);

  // ── Per-parameter "Tested" flags (default OFF) ────────────────────────────
  // When false the field is null in the saved result; Action Plan ignores it.
  const [fcTested, setFcTested] = useState(false);
  const [bromTested, setBromTested] = useState(false);
  const [phTested, setPhTested] = useState(false);
  const [taTested, setTaTested] = useState(false);
  const [chTested, setChTested] = useState(false);
  const [cyaTested, setCyaTested] = useState(false);

  const calculateResults = useCallback(
    (): TestResult => ({
      fc: fcTested ? parseFloat((fcDrops * fcSampleSize).toFixed(1)) : null,
      cc: fcTested ? parseFloat((ccDrops * fcSampleSize).toFixed(1)) : null,
      ph: phTested ? ph : null,
      ta: taTested ? Math.round(taDrops * 10) : null,
      ch: chTested ? Math.round(chDrops * 10) : null,
      cya: cyaTested ? cya : null,
      bromine: bromTested ? bromine : null,
      acid_demand_drops: acidDemandDrops,
      base_demand_drops: baseDemandDrops,
      temp,
      tds,
    }),
    [fcTested, bromTested, phTested, taTested, chTested, cyaTested,
     fcDrops, fcSampleSize, ccDrops, ph, taDrops, chDrops, cya, bromine, acidDemandDrops, baseDemandDrops, temp, tds],
  );

  const results = calculateResults();
  const handleSave = () => onSave(results);
  const visibleTests = KITS[kit]?.tests ?? KITS['K-2006']?.tests ?? [];

  // Adjusted ideal ranges for hot tubs vs pools
  const fcLow = isHotTub ? 3 : 2;
  const fcHigh = isHotTub ? 5 : 6;
  const chLowLabel = isHotTub ? '< 150' : '< 200';
  const chHighLabel = isHotTub ? '150-250' : '200-400';
  const cyaLow = isHotTub ? 30 : 30;
  const cyaHigh = isHotTub ? 50 : 50;

  // Group kits by brand for the selector
  const brandGroups = new Map<string, Array<[string, (typeof KITS)[string]]>>();
  for (const [id, k] of Object.entries(KITS)) {
    const brand = k.brand ?? 'Other';
    if (!brandGroups.has(brand)) brandGroups.set(brand, []);
    brandGroups.get(brand)!.push([id, k]);
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Water Body Banner */}
      {isHotTub && (
        <div className="bg-orange-900/40 border border-orange-500 rounded-lg p-3 text-center">
          <p className="text-orange-300 font-semibold text-sm">
            ♨️ Hot Tub Mode — Ideal ranges adjusted for spa chemistry
          </p>
        </div>
      )}

      {/* Kit Selector */}
      <Card>
        <label htmlFor="kitSelector" className="block text-lg font-medium text-white">
          Select Your Test Kit
        </label>
        <select
          id="kitSelector"
          value={kit}
          onChange={(e) => setKit(e.target.value)}
          className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white focus:ring-cyan-500 focus:border-cyan-500"
        >
          {Array.from(brandGroups.entries()).map(([brand, kits]) => (
            <optgroup key={brand} label={brand}>
              {kits.map(([id, k]) => (
                <option key={id} value={id}>
                  {id} — {k.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </Card>

      {/* Volume */}
      <Card>
        <label htmlFor="poolVolume" className="block text-lg font-medium text-white">
          {isHotTub ? 'Hot Tub Volume (Gallons)' : 'Pool Volume (Gallons)'}
        </label>
        <p className="text-sm text-slate-400 mb-2">Required for accurate chemical dosage recommendations.</p>
        <input
          type="number"
          id="poolVolume"
          value={poolVolume}
          onChange={(e) => setPoolVolume(Number(e.target.value))}
          className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white focus:ring-cyan-500 focus:border-cyan-500"
          placeholder={isHotTub ? 'e.g., 400' : 'e.g., 15000'}
        />
      </Card>

      {/* Temp & TDS */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <label htmlFor="temp_value" className="block text-sm font-medium text-white">
            Water Temperature (°F)
          </label>
          <input
            type="number"
            id="temp_value"
            value={temp}
            onChange={(e) => setTemp(Number(e.target.value))}
            className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
            placeholder={isHotTub ? 'e.g., 102' : 'e.g., 82'}
          />
          {isHotTub && (
            <p className="text-xs text-orange-400 mt-1">Hot tub safe range: 98–104°F (37–40°C)</p>
          )}
        </Card>
        <Card>
          <label htmlFor="tds_value" className="block text-sm font-medium text-white">
            Total Dissolved Solids (ppm)
          </label>
          <input
            type="number"
            id="tds_value"
            value={tds}
            onChange={(e) => setTds(Number(e.target.value))}
            className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
            placeholder="Optional, defaults to 500"
          />
          {isHotTub && (
            <p className="text-xs text-slate-400 mt-1">Drain &amp; refill when TDS exceeds 1500 ppm above fill water</p>
          )}
        </Card>
      </div>

      {/* Bromine — shown first / prominently for hot tubs */}
      {visibleTests.includes('bromine') && (
        <Card>
          <Details title={<h2 className="text-xl font-semibold text-white">🟫 Total Bromine {isHotTub && <span className="text-orange-400 text-sm ml-2">(Primary Sanitizer)</span>}</h2>}>
            <p><strong className="text-cyan-400">Reagents:</strong> R-0001, R-0002</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Fill small tube to <strong>9 mL</strong> with sample water.</li>
              <li>Add 5 drops R-0001 and 5 drops R-0002. Cap and invert.</li>
              <li>Compare color to the standards chart. Record ppm.</li>
            </ol>
            {isHotTub && (
              <p className="text-orange-300 text-xs mt-2">
                💡 Hot tubs use bromine for its stability at high temperatures. Target 3–5 ppm.
              </p>
            )}
          </Details>
          <div className="flex justify-end mt-2">
            <TestedBadge tested={bromTested} onToggle={setBromTested} />
          </div>
          <div className="mt-2">
            <label htmlFor="bromine_value" className="block text-sm font-medium text-slate-400">
              Enter Bromine Value (ppm)
            </label>
            <input
              type="number"
              step="0.5"
              id="bromine_value"
              value={bromine}
              onChange={(e) => { setBromine(Number(e.target.value)); setBromTested(true); }}
              className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
            />
          </div>
          <Gauge
            value={bromine}
            min={0}
            max={10}
            ranges={[
              { color: 'bg-red-600', width: '20%', label: '< 2' },
              { color: 'bg-green-500', width: '40%', label: '2–6' },
              { color: 'bg-orange-500', width: '40%', label: '> 6' },
            ]}
          />
        </Card>
      )}

      {/* Free & Combined Chlorine */}
      {visibleTests.includes('chlorine') && (
        <Card>
          <Details title={<h2 className="text-xl font-semibold text-white">🧪 Free &amp; Combined Chlorine</h2>}>
            <p><strong className="text-cyan-400">Reagents:</strong> R-0870, R-0871, R-0003</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Fill large tube (25 mL or 10 mL).</li>
              <li>Add 2 dippers of R-0870 powder and swirl. Pink = FC present.</li>
              <li><strong>FC:</strong> Add R-0871 dropwise until colorless. Enter drops below.</li>
              <li><strong>CC:</strong> Add 5 drops R-0003, swirl. If pink again, add R-0871 until clear.</li>
            </ol>
            {isHotTub && (
              <p className="text-orange-300 text-xs mt-2">
                💡 If using bromine as primary sanitizer, FC may read 0 — that is normal.
              </p>
            )}
          </Details>
          <div className="flex justify-end mt-2">
            <TestedBadge tested={fcTested} onToggle={setFcTested} />
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fc_sample_size" className="block text-sm font-medium text-slate-400">
                Sample Size
              </label>
              <select
                id="fc_sample_size"
                value={fcSampleSize}
                onChange={(e) => setFcSampleSize(Number(e.target.value))}
                className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
              >
                <option value="0.5">10 mL (0.5 ppm/drop)</option>
                <option value="0.2">25 mL (0.2 ppm/drop)</option>
              </select>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fc_drops" className="block text-sm font-medium text-slate-400">
                Drops for FC
              </label>
              <input
                type="number"
                id="fc_drops"
                value={fcDrops}
                onChange={(e) => { setFcDrops(Number(e.target.value)); setFcTested(true); }}
                className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
              />
            </div>
            <div className="bg-slate-900/50 p-3 rounded-md text-center flex flex-col justify-center">
              <span className="text-sm text-cyan-400">Free Chlorine</span>
              <span className="text-2xl font-bold">{(fcDrops * fcSampleSize).toFixed(1)} ppm</span>
            </div>
          </div>
          <Gauge
            value={fcDrops * fcSampleSize}
            min={0}
            max={10}
            ranges={[
              { color: 'bg-red-600', width: `${fcLow * 10}%`, label: `< ${fcLow}` },
              { color: 'bg-green-500', width: `${(fcHigh - fcLow) * 10}%`, label: `${fcLow}–${fcHigh}` },
              { color: 'bg-orange-500', width: `${(10 - fcHigh) * 10}%`, label: `> ${fcHigh}` },
            ]}
          />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cc_drops" className="block text-sm font-medium text-slate-400">
                Drops for CC
              </label>
              <input
                type="number"
                id="cc_drops"
                value={ccDrops}
                onChange={(e) => { setCcDrops(Number(e.target.value)); setFcTested(true); }}
                className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
              />
            </div>
            <div className="bg-slate-900/50 p-3 rounded-md text-center flex flex-col justify-center">
              <span className="text-sm text-cyan-400">Combined Chlorine</span>
              <span className="text-2xl font-bold">{(ccDrops * fcSampleSize).toFixed(1)} ppm</span>
            </div>
          </div>
          <Gauge
            value={ccDrops * fcSampleSize}
            min={0}
            max={1}
            ranges={[
              { color: 'bg-green-500', width: '50%', label: 'Good (<0.5)' },
              { color: 'bg-red-600', width: '50%', label: 'High (Shock)' },
            ]}
          />
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* pH */}
        {visibleTests.includes('ph') && (
          <Card className="flex flex-col">
            <Details title={<h2 className="text-xl font-semibold text-white">⚗️ pH</h2>}>
              <p><strong className="text-cyan-400">Reagent:</strong> R-0004</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Fill large tube to 44 mL mark.</li>
                <li>Add 5 drops of R-0004. Cap and invert.</li>
                <li>Compare color to standards.</li>
              </ol>
              {isHotTub && (
                <p className="text-orange-300 text-xs mt-2">
                  💡 Hot tub pH drifts up faster due to aeration. Ideal: 7.2–7.6.
                </p>
              )}
            </Details>
            <div className="flex justify-end mt-2">
              <TestedBadge tested={phTested} onToggle={setPhTested} />
            </div>
            <div className="mt-2 flex-grow">
              <label htmlFor="ph_value" className="block text-sm font-medium text-slate-400">
                Enter pH Value
              </label>
              <input
                type="number"
                step="0.1"
                id="ph_value"
                value={ph}
                onChange={(e) => { setPh(Number(e.target.value)); setPhTested(true); }}
                className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
              />
            </div>
            <Gauge
              value={ph}
              min={7.0}
              max={8.0}
              ranges={[
                { color: 'bg-red-600', width: '20%', label: '< 7.2' },
                { color: 'bg-green-500', width: '60%', label: '7.2–7.8' },
                { color: 'bg-orange-500', width: '20%', label: '> 7.8' },
              ]}
            />
            <Details
              title={<span className="text-sm font-semibold text-cyan-400">Advanced: Acid/Base Demand Test</span>}
              className="mt-4 bg-slate-900/50 rounded-lg p-3"
            >
              <div className="text-xs space-y-3">
                <div>
                  <p className="font-bold">Acid Demand (to LOWER pH)</p>
                  <ol className="list-decimal list-inside space-y-1 mt-1">
                    <li>Use treated sample from pH test.</li>
                    <li>Add R-0005 (Red cap) dropwise until desired pH matched.</li>
                  </ol>
                  <div className="mt-2">
                    <label htmlFor="acid_demand_drops" className="block text-xs font-medium text-slate-400">
                      Drops of R-0005
                    </label>
                    <input
                      type="number"
                      value={acidDemandDrops}
                      onChange={(e) => setAcidDemandDrops(Number(e.target.value))}
                      id="acid_demand_drops"
                      className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                  <p className="font-bold">Base Demand (to RAISE pH)</p>
                  <ol className="list-decimal list-inside space-y-1 mt-1">
                    <li>Use treated sample from pH test.</li>
                    <li>Add R-0006 dropwise until desired pH matched.</li>
                  </ol>
                  <div className="mt-2">
                    <label htmlFor="base_demand_drops" className="block text-xs font-medium text-slate-400">
                      Drops of R-0006
                    </label>
                    <input
                      type="number"
                      value={baseDemandDrops}
                      onChange={(e) => setBaseDemandDrops(Number(e.target.value))}
                      id="base_demand_drops"
                      className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white text-sm"
                    />
                  </div>
                </div>
              </div>
            </Details>
          </Card>
        )}

        {/* TA */}
        {visibleTests.includes('ta') && (
          <Card>
            <Details title={<h2 className="text-xl font-semibold text-white">🧂 Total Alkalinity</h2>}>
              <p><strong className="text-cyan-400">Reagents:</strong> R-0007, R-0008, R-0009</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Fill large tube to 25 mL.</li>
                <li>Add 2 drops R-0007, then 5 drops R-0008 (turns green).</li>
                <li>Add R-0009 dropwise, counting, until color turns red.</li>
              </ol>
            </Details>
            <div className="flex justify-end mt-2">
              <TestedBadge tested={taTested} onToggle={setTaTested} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="ta_drops" className="block text-sm font-medium text-slate-400">
                  Drops
                </label>
                <input
                  type="number"
                  id="ta_drops"
                  value={taDrops}
                  onChange={(e) => { setTaDrops(Number(e.target.value)); setTaTested(true); }}
                  className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
                />
              </div>
              <div className="bg-slate-900/50 p-3 rounded-md text-center flex flex-col justify-center">
                <span className="text-sm text-cyan-400">TA Result</span>
                <span className="text-2xl font-bold">{Math.round(taDrops * 10)} ppm</span>
              </div>
            </div>
            <Gauge
              value={Math.round(taDrops * 10)}
              min={50}
              max={150}
              ranges={[
                { color: 'bg-red-600', width: '30%', label: '< 80' },
                { color: 'bg-green-500', width: '40%', label: '80–120' },
                { color: 'bg-orange-500', width: '30%', label: '> 120' },
              ]}
            />
          </Card>
        )}

        {/* CH */}
        {visibleTests.includes('ch') && (
          <Card>
            <Details title={<h2 className="text-xl font-semibold text-white">💎 Calcium Hardness</h2>}>
              <p><strong className="text-cyan-400">Reagents:</strong> R-0010, R-0011L, R-0012</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Fill large tube to 25 mL.</li>
                <li>Add 20 drops R-0010, then 5 drops R-0011L (turns red).</li>
                <li>Add R-0012 dropwise until color changes to blue.</li>
              </ol>
              {isHotTub && (
                <p className="text-orange-300 text-xs mt-2">
                  💡 Hot tub target: 150–250 ppm (acrylic shells tolerate lower CH).
                </p>
              )}
            </Details>
            <div className="flex justify-end mt-2">
              <TestedBadge tested={chTested} onToggle={setChTested} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="ch_drops" className="block text-sm font-medium text-slate-400">
                  Drops
                </label>
                <input
                  type="number"
                  id="ch_drops"
                  value={chDrops}
                  onChange={(e) => { setChDrops(Number(e.target.value)); setChTested(true); }}
                  className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
                />
              </div>
              <div className="bg-slate-900/50 p-3 rounded-md text-center flex flex-col justify-center">
                <span className="text-sm text-cyan-400">CH Result</span>
                <span className="text-2xl font-bold">{Math.round(chDrops * 10)} ppm</span>
              </div>
            </div>
            <Gauge
              value={Math.round(chDrops * 10)}
              min={isHotTub ? 50 : 100}
              max={isHotTub ? 400 : 500}
              ranges={[
                { color: 'bg-red-600', width: '25%', label: chLowLabel },
                { color: 'bg-green-500', width: '50%', label: chHighLabel },
                { color: 'bg-orange-500', width: '25%', label: isHotTub ? '> 250' : '> 400' },
              ]}
            />
          </Card>
        )}

        {/* CYA */}
        {visibleTests.includes('cya') && (
          <Card>
            <Details title={<h2 className="text-xl font-semibold text-white">🕶️ Cyanuric Acid (CYA)</h2>}>
              <p><strong className="text-cyan-400">Reagent:</strong> R-0013</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Fill bottle to 7 mL, add R-0013 to 14 mL. Mix 30s.</li>
                <li>Transfer to small tube until black dot disappears.</li>
                <li>Read ppm level from tube scale.</li>
              </ol>
              {isHotTub && (
                <p className="text-orange-300 text-xs mt-2">
                  💡 If using bromine (no CYA needed), or dichlor chlorine target 30–50 ppm max.
                </p>
              )}
            </Details>
            <div className="flex justify-end mt-2">
              <TestedBadge tested={cyaTested} onToggle={setCyaTested} />
            </div>
            <div className="mt-2">
              <label htmlFor="cya_value" className="block text-sm font-medium text-slate-400">
                Enter CYA Value (ppm)
              </label>
              <input
                type="number"
                id="cya_value"
                value={cya}
                onChange={(e) => { setCya(Number(e.target.value)); setCyaTested(true); }}
                className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
              />
            </div>
            <Gauge
              value={cya}
              min={20}
              max={80}
              ranges={[
                { color: 'bg-red-600', width: '16.6%', label: `< ${cyaLow}` },
                { color: 'bg-green-500', width: '33.3%', label: `${cyaLow}–${cyaHigh}` },
                { color: 'bg-orange-500', width: '50%', label: `> ${cyaHigh}` },
              ]}
            />
          </Card>
        )}
      </div>

      {/* Save Button */}
      <div className="pt-4">
        <button
          onClick={handleSave}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          Save Test &amp; View Plans
        </button>
      </div>
    </div>
  );
};
